import React from "react";
import { Button } from "../ui/button";
import { useToast } from "../ui/use-toast";
import { Shuffle, Undo, SendHorizontal } from "lucide-react";
import {
  isGuessCorrect,
  isGuessRepeated,
  shuffleGameData,
} from "../../lib/game-helpers";

import { GameStatusContext } from "../../providers/GameStatusProvider";
import { PuzzleDataContext } from "../../providers/PuzzleDataProvider";

function GameControlButtonsPanel({
  shuffledRows,
  setShuffledRows,
  setGridShake,
}) {
  const {
    isGameOver,
    guessCandidate,
    setGuessCandidate,
    submittedGuesses,
    setSubmittedGuesses,
    solvedGameData,
    setSolvedGameData,
  } = React.useContext(GameStatusContext);
  const { gameData, categorySize } = React.useContext(PuzzleDataContext);
  const { toast } = useToast();
  const isCandidateReady = guessCandidate.length === categorySize;

  function deselectAll() {
    setGuessCandidate([]);
  }

  function submitCandidateGuess() {
    // check that its a valid guess by size
    if (guessCandidate.length !== categorySize) {
      return;
    }
    // check that the guess hasnt already been submitted previously
    if (isGuessRepeated({ submittedGuesses, guessCandidate })) {
      toast({
        label: "Notification",
        title: "Repeated Guess",
        description: "You previously made this guess!",
      });

      return;
    }
    // add guess to state
    setSubmittedGuesses([...submittedGuesses, guessCandidate]);
    // check if the guess is correct
    const {
      isCorrect,
      correctWords,
      correctCategory,
      isGuessOneAway,
      correctDifficulty,
      correctImageSrc,
    } = isGuessCorrect({
      guessCandidate,
      gameData,
    });

    // if the guess is correct:
    // set it as solved in game data
    if (isCorrect) {
      setSolvedGameData([
        ...solvedGameData,
        {
          category: correctCategory,
          words: correctWords,
          difficulty: correctDifficulty,
          imageSrc: correctImageSrc,
        },
      ]);
      setGuessCandidate([]);
    } else {
      // Shake the grid to give feedback that they were wrong
      setGridShake(true);
      if (isGuessOneAway) {
        toast({
          label: "Notification",
          title: "Close Guess",
          description:
            "You were one guess away from correctly guessing a category!",
        });
      }
    }
  }

  return (
    <>
      <div className="md:hidden h-24" />
      <div className="fixed bottom-0 inset-x-0 z-20 border-t border-border bg-background/95 backdrop-blur-sm md:static md:z-auto md:border-0 md:bg-transparent md:backdrop-blur-none">
        <div className="mx-auto w-full max-w-[500px] px-4 py-3 md:p-0">
          <div className="grid grid-cols-3 gap-3 md:gap-4">
            <Button
              disabled={isGameOver}
              variant="secondary"
              onClick={() =>
                setShuffledRows(shuffleGameData({ gameData: shuffledRows }))
              }
            >
              <span className="inline-flex items-center justify-center gap-2 w-full leading-none">
                <Shuffle className="h-4 w-4 shrink-0" strokeWidth={1} />
                <p className="select-none leading-none">Shuffle</p>
              </span>
            </Button>
            <Button
              size="deselectallsize"
              disabled={isGameOver}
              variant="secondary"
              className="bg-amber-700 text-amber-50 border border-amber-800 hover:bg-amber-600 dark:bg-amber-600 dark:text-amber-50 dark:border-amber-700 dark:hover:bg-amber-500"
              onClick={deselectAll}
            >
              <span className="inline-flex items-center justify-center gap-2 w-full leading-none">
                <Undo className="h-4 w-4 shrink-0" strokeWidth={1} />
                <p className="select-none leading-none">Clear</p>
              </span>
            </Button>
            <Button
              variant="submit"
              className={isCandidateReady ? "submit-ready-nudge" : ""}
              onClick={submitCandidateGuess}
              disabled={isGameOver || !isCandidateReady}
            >
              <span className="inline-flex items-center justify-center gap-2 w-full leading-none">
                <SendHorizontal className="h-4 w-4 shrink-0" strokeWidth={1} />
                <p className="select-none leading-none">Submit</p>
              </span>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export default GameControlButtonsPanel;
